import { CanvasTexture, SRGBColorSpace, type Texture } from 'three';
import { faceUVMap, eraseTriangles, type FaceUVPlacement } from './faceUVMap';

const SIZE = 1024;
const FONT_STACK = 'system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';

export class MessageTextureGenerator {
  private sourceImage: HTMLImageElement | ImageBitmap | HTMLCanvasElement;

  constructor(sourceImage: HTMLImageElement | ImageBitmap | HTMLCanvasElement) {
    this.sourceImage = sourceImage;
  }

  generate(messages: string[], overrides?: Partial<FaceUVPlacement>[]): CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d')!;

    ctx.drawImage(this.sourceImage, 0, 0, SIZE, SIZE);

    for (let i = 0; i < faceUVMap.length; i++) {
      const fillColor = this.sampleFaceColor(ctx, i);
      this.eraseFace(ctx, i, fillColor);
    }

    for (let i = 0; i < faceUVMap.length; i++) {
      const base = faceUVMap[i];
      const o = overrides?.[i];
      const placement: FaceUVPlacement = {
        center: o?.center ?? base.center,
        rotation: o?.rotation ?? base.rotation,
        radius: o?.radius ?? base.radius,
      };
      const text = messages[i] ?? '';
      if (text.trim().length > 0) {
        this.drawText(ctx, text, placement);
      }
    }

    const texture = new CanvasTexture(canvas);
    texture.flipY = false;
    texture.colorSpace = SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
  }

  private sampleFaceColor(ctx: CanvasRenderingContext2D, faceIndex: number): string {
    const { center, radius } = faceUVMap[faceIndex];
    const cx = center[0] * SIZE;
    const cy = center[1] * SIZE;
    const r = radius * SIZE * 0.7;

    const samples: [number, number, number][] = [];
    for (let k = 0; k < 24; k++) {
      const angle = (k / 24) * Math.PI * 2;
      const dist = r * (0.3 + 0.7 * ((k % 3) / 3));
      const x = Math.round(cx + Math.cos(angle) * dist);
      const y = Math.round(cy + Math.sin(angle) * dist);
      if (x < 0 || y < 0 || x >= SIZE || y >= SIZE) continue;
      const d = ctx.getImageData(x, y, 1, 1).data;
      samples.push([d[0], d[1], d[2]]);
    }

    if (samples.length === 0) return '#2929d6';

    const channel = (ch: number) => {
      const vals = samples.map((s) => s[ch]).sort((a, b) => a - b);
      return vals[Math.floor(vals.length / 2)];
    };
    return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
  }

  private eraseFace(ctx: CanvasRenderingContext2D, faceIndex: number, color: string): void {
    const tris = eraseTriangles[faceIndex];
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    for (const t of tris) {
      ctx.moveTo(t[0] * SIZE, t[1] * SIZE);
      ctx.lineTo(t[2] * SIZE, t[3] * SIZE);
      ctx.lineTo(t[4] * SIZE, t[5] * SIZE);
      ctx.closePath();
    }
    ctx.fill();
    // Expand slightly to cover antialiased edges of old text near boundaries
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }

  private drawText(ctx: CanvasRenderingContext2D, text: string, placement: FaceUVPlacement): void {
    const cx = placement.center[0] * SIZE;
    const cy = placement.center[1] * SIZE;
    const maxWidth = placement.radius * SIZE * 1.5;
    const maxHeight = placement.radius * SIZE * 1.3;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(placement.rotation);
    ctx.fillStyle = '#e8eef7';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    let fontSize = Math.floor(maxHeight / 2);
    let lines: string[] = [];

    while (fontSize > 6) {
      ctx.font = `600 ${fontSize}px ${FONT_STACK}`;
      lines = this.wrapText(ctx, text, maxWidth);
      const totalHeight = lines.length * fontSize * 1.15;
      const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
      if (totalHeight <= maxHeight && widest <= maxWidth) break;
      fontSize -= 1;
    }

    const lineHeight = fontSize * 1.15;
    const startY = -((lines.length - 1) * lineHeight) / 2;
    for (let li = 0; li < lines.length; li++) {
      ctx.fillText(lines[li], 0, startY + li * lineHeight);
    }

    ctx.restore();
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const explicit = text.split('\n');
    const lines: string[] = [];

    for (const seg of explicit) {
      const words = seg.split(/\s+/).filter((w) => w.length > 0);
      if (words.length === 0) continue;

      let current = words[0];
      for (let i = 1; i < words.length; i++) {
        const candidate = current + ' ' + words[i];
        if (ctx.measureText(candidate).width <= maxWidth) {
          current = candidate;
        } else {
          lines.push(current);
          current = words[i];
        }
      }
      lines.push(current);
    }

    return lines.length > 0 ? lines : [text];
  }
}

export function disposeGeneratedTexture(texture: Texture | null): void {
  texture?.dispose();
}
